import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Phone, Mail, FileText, StickyNote, CheckCircle2, ChevronLeft, ArrowRightLeft, Star, Send } from "lucide-react";
import { getContact, logCommunication, sendCrmEmail, setContactStage, upsertTask, completeTask, requestReviewForContact, PIPELINE_STAGES } from "@/lib/crm.functions";

export const Route = createFileRoute("/_authenticated/admin/contacts/$id")({
  component: ContactDetail,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");
const fmtUsd = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function ContactDetail() {
  const { id } = Route.useParams();
  const load = useServerFn(getContact);
  const logComm = useServerFn(logCommunication);
  const sendEmail = useServerFn(sendCrmEmail);
  const setStage = useServerFn(setContactStage);
  const addTask = useServerFn(upsertTask);
  const doneTask = useServerFn(completeTask);
  const askReview = useServerFn(requestReviewForContact);
  const qc = useQueryClient();
  const [tab, setTab] = useState<"log_call" | "note" | "email" | "task">("log_call");

  const { data, isLoading } = useQuery({ queryKey: ["crm", "contact", id], queryFn: () => load({ data: { id } }) });

  const stageMut = useMutation({
    mutationFn: (stage: string) => setStage({ data: { id, stage } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });
  const commMut = useMutation({
    mutationFn: (v: any) => logComm({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });
  const emailMut = useMutation({
    mutationFn: (v: any) => sendEmail({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });
  const taskMut = useMutation({
    mutationFn: (v: any) => addTask({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });
  const taskDoneMut = useMutation({
    mutationFn: (tid: string) => doneTask({ data: { id: tid, done: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });
  const reviewMut = useMutation({
    mutationFn: () => askReview({ data: { contact_id: id, origin: typeof window !== "undefined" ? window.location.origin : undefined } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!data) return null;
  const { contact, communications, tasks, stageEvents, estimates, proposals, projects, invoices, reviewRequests = [], opportunities = [] } = data as any;

  // build unified timeline
  const events: { ts: string; kind: string; title: string; body?: string; meta?: string; icon: any }[] = [];
  for (const c of communications) {
    events.push({
      ts: c.occurred_at,
      kind: c.kind,
      title: c.kind === "email" ? `Email · ${c.subject ?? "(no subject)"}` : c.kind === "call" ? `Call · ${c.direction ?? "outbound"}` : "Note",
      body: c.body ?? undefined,
      meta: c.outcome ?? undefined,
      icon: c.kind === "email" ? Mail : c.kind === "call" ? Phone : StickyNote,
    });
  }
  for (const e of stageEvents) {
    events.push({ ts: e.created_at, kind: "stage", title: `Stage → ${e.to_stage}`, meta: e.from_stage ? `from ${e.from_stage}` : undefined, icon: ArrowRightLeft });
  }
  for (const est of estimates) {
    events.push({ ts: est.created_at, kind: "estimate", title: `AI Estimate · ${est.project_type}`, meta: `${fmtUsd(Number(est.price_low ?? 0))} – ${fmtUsd(Number(est.price_high ?? 0))}`, icon: FileText });
  }
  for (const p of proposals) {
    events.push({ ts: p.created_at, kind: "proposal", title: `Proposal · ${p.project_type ?? p.customer_name ?? ""}`, meta: `${fmtUsd(Number(p.total ?? 0))} · ${p.status}`, icon: FileText });
  }
  for (const o of opportunities as any[]) {
    if (o.won_at) {
      events.push({ ts: o.won_at, kind: "opp", title: `Opportunity won · ${o.project_title ?? o.service_type ?? ""}`, meta: fmtUsd(Number(o.opportunity_value ?? 0)), icon: CheckCircle2 });
    } else if (o.lost_at) {
      events.push({ ts: o.lost_at, kind: "opp", title: `Opportunity lost · ${o.project_title ?? o.service_type ?? ""}`, meta: o.lost_reason ?? undefined, icon: ArrowRightLeft });
    }
  }
  for (const rr of reviewRequests as any[]) {
    const rating = rr.customer_reviews?.[0]?.rating;
    events.push({
      ts: rr.sent_at ?? rr.created_at,
      kind: "review",
      title: `Review request · ${rr.trigger_source}`,
      meta: rating ? `${rating}★ received` : rr.status,
      icon: Star,
    });
  }
  events.sort((a, b) => (a.ts < b.ts ? 1 : -1));

  const openTasks = tasks.filter((t: any) => t.status === "open");
  const doneTasks = tasks.filter((t: any) => t.status !== "open");

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Link to="/admin/contacts" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-amber-300"><ChevronLeft className="size-3.5" /> All contacts</Link>

      <header className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl text-white">{contact.name ?? contact.email ?? "Unnamed"}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-zinc-400">
              {contact.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 hover:text-amber-300"><Mail className="size-3.5" /> {contact.email}</a>}
              {contact.phone && <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1 hover:text-amber-300"><Phone className="size-3.5" /> {contact.phone}</a>}
              {contact.city && <span>{contact.city}{contact.state ? `, ${contact.state}` : ""}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <select value={contact.pipeline_stage} onChange={(e) => stageMut.mutate(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
              {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>Stage: {s.label}</option>)}
            </select>
            <p className="text-[11px] text-zinc-500">LTV {fmtUsd(Number(contact.lifetime_value ?? 0))} · Open {fmtUsd(Number(contact.open_balance ?? 0))}</p>
            <button
              onClick={() => {
                if (!contact.email) { alert("Add an email to this contact first."); return; }
                if (confirm(`Send a review request email to ${contact.email}?`)) reviewMut.mutate();
              }}
              disabled={reviewMut.isPending || !contact.email}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-300/20 disabled:opacity-50"
            >
              {reviewMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              Request review
            </button>
          </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Action panel */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex border-b border-white/5 text-sm">
              {[
                { k: "log_call", label: "Log call", icon: Phone },
                { k: "note", label: "Note", icon: StickyNote },
                { k: "email", label: "Email", icon: Mail },
                { k: "task", label: "Task", icon: CheckCircle2 },
              ].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k as any)}
                  className={`flex-1 px-3 py-3 flex items-center justify-center gap-2 ${tab === t.k ? "text-amber-200 border-b-2 border-amber-300" : "text-zinc-400 hover:text-white"}`}>
                  <t.icon className="size-4" /> {t.label}
                </button>
              ))}
            </div>
            <div className="p-5">
              {tab === "log_call" && (
                <CallForm pending={commMut.isPending} onSubmit={(v) => commMut.mutate({ contact_id: id, kind: "call", ...v })} />
              )}
              {tab === "note" && (
                <NoteForm pending={commMut.isPending} onSubmit={(v) => commMut.mutate({ contact_id: id, kind: "note", ...v })} />
              )}
              {tab === "email" && (
                <EmailForm defaultTo={contact.email ?? ""} pending={emailMut.isPending} error={emailMut.data && !emailMut.data.ok ? emailMut.data.error ?? "Email skipped" : undefined}
                  onSubmit={(v) => emailMut.mutate({ contact_id: id, ...v })} />
              )}
              {tab === "task" && (
                <TaskForm pending={taskMut.isPending} onSubmit={(v) => taskMut.mutate({ contact_id: id, ...v })} />
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="font-serif text-lg text-white mb-4">Activity timeline</h2>
            {events.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No activity yet. Log the first call or note above.</p>
            ) : (
              <ol className="relative border-l border-white/10 pl-5 space-y-4">
                {events.map((e, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[26px] top-1 size-3 rounded-full bg-amber-300/70 ring-4 ring-[#030303]" />
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                      <e.icon className="size-3.5 text-amber-300" />
                      <span>{fmt(e.ts)}</span>
                      {e.meta && <span className="text-zinc-500">· {e.meta}</span>}
                    </div>
                    <p className="text-sm text-white mt-1">{e.title}</p>
                    {e.body && <p className="mt-1 text-sm text-zinc-400 whitespace-pre-wrap">{e.body}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <aside className="space-y-6">
          <Sidebar title="Open tasks">
            {openTasks.length === 0 ? <Empty text="No open tasks." /> : (
              <ul className="space-y-2">
                {openTasks.map((t: any) => (
                  <li key={t.id} className="flex items-start gap-2">
                    <button onClick={() => taskDoneMut.mutate(t.id)} className="mt-0.5 size-4 rounded-full border border-white/20 hover:bg-amber-300/20" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white">{t.title}</p>
                      <p className="text-[11px] text-zinc-500">Due {fmt(t.due_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Estimates">
            {estimates.length === 0 ? <Empty text="No AI estimates on file." /> : (
              <ul className="space-y-2 text-sm">
                {estimates.map((e: any) => (
                  <li key={e.id} className="flex justify-between gap-2">
                    <span className="text-zinc-300 truncate">{e.project_type}</span>
                    <span className="text-zinc-500 shrink-0">{fmtUsd(Number(e.price_low))}–{fmtUsd(Number(e.price_high))}</span>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Opportunities">
            {opportunities.length === 0 ? <Empty text="No opportunities yet." /> : (
              <ul className="space-y-2 text-sm">
                {(opportunities as any[]).map((o) => (
                  <li key={o.id} className="flex justify-between gap-2">
                    <span className="text-zinc-300 truncate capitalize">{o.project_title ?? o.service_type ?? "Opportunity"}</span>
                    <span className="shrink-0 text-zinc-400">
                      {fmtUsd(Number(o.opportunity_value ?? 0))}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500">{o.stage}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Proposals">
            {proposals.length === 0 ? <Empty text="No proposals sent." /> : (
              <ul className="space-y-2 text-sm">
                {proposals.map((p: any) => (
                  <li key={p.id} className="flex justify-between gap-2">
                    <span className="text-zinc-300 truncate">{p.project_type ?? p.customer_name}</span>
                    <span className="text-zinc-500 shrink-0">{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Projects">
            {projects.length === 0 ? <Empty text="No projects." /> : (
              <ul className="space-y-2 text-sm">
                {projects.map((p: any) => (
                  <li key={p.id} className="flex justify-between gap-2">
                    <span className="text-zinc-300 truncate">{p.project_name}</span>
                    <span className="text-zinc-500 shrink-0">{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Invoices">
            {invoices.length === 0 ? <Empty text="No invoices." /> : (
              <ul className="space-y-2 text-sm">
                {invoices.map((i: any) => (
                  <li key={i.id} className="flex justify-between gap-2">
                    <span className="text-zinc-300 truncate">{i.invoice_number}</span>
                    <span className="text-zinc-500 shrink-0">{fmtUsd(Number(i.balance_due ?? 0))} open</span>
                  </li>
                ))}
              </ul>
            )}
          </Sidebar>

          <Sidebar title="Review requests">
            {reviewRequests.length === 0 ? (
              <Empty text="No review requests sent yet." />
            ) : (
              <ul className="space-y-2 text-sm">
                {(reviewRequests as any[]).map((r) => {
                  const rating = r.customer_reviews?.[0]?.rating;
                  return (
                    <li key={r.id} className="flex flex-col gap-0.5">
                      <div className="flex justify-between gap-2">
                        <span className="text-zinc-300 capitalize truncate">{r.trigger_source}</span>
                        <span className="text-zinc-500 shrink-0 capitalize">{rating ? `${rating}★` : r.status}</span>
                      </div>
                      <span className="text-[11px] text-zinc-500">
                        Sent {fmtDate(r.sent_at ?? r.created_at)}
                        {r.reminder_count ? ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}` : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Sidebar>

          {doneTasks.length > 0 && (
            <Sidebar title="Completed tasks">
              <ul className="space-y-1 text-sm">
                {doneTasks.slice(0, 6).map((t: any) => (
                  <li key={t.id} className="text-zinc-500 line-through truncate">{t.title}</li>
                ))}
              </ul>
            </Sidebar>
          )}
        </aside>
      </div>
    </div>
  );
}

function Sidebar({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{title}</p>
      {children}
    </div>
  );
}
function Empty({ text }: { text: string }) { return <p className="text-xs text-zinc-500 py-2">{text}</p>; }

function CallForm({ onSubmit, pending }: { onSubmit: (v: any) => void; pending: boolean }) {
  const [f, setF] = useState({ direction: "outbound", outcome: "", body: "", duration_seconds: 0 });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); setF({ direction: "outbound", outcome: "", body: "", duration_seconds: 0 }); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
          <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
        </select>
        <input placeholder="Outcome (e.g. left voicemail)" value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      </div>
      <textarea placeholder="Call notes" rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      <button disabled={pending} className="bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60">{pending ? "Saving…" : "Log call"}</button>
    </form>
  );
}
function NoteForm({ onSubmit, pending }: { onSubmit: (v: any) => void; pending: boolean }) {
  const [body, setBody] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) { onSubmit({ body }); setBody(""); } }} className="space-y-3">
      <textarea placeholder="Write a note…" rows={4} value={body} onChange={(e) => setBody(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      <button disabled={pending || !body.trim()} className="bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60">Save note</button>
    </form>
  );
}
function EmailForm({ defaultTo, onSubmit, pending, error }: { defaultTo: string; onSubmit: (v: any) => void; pending: boolean; error?: string }) {
  const [f, setF] = useState({ to: defaultTo, subject: "", body: "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <input type="email" placeholder="To" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      <input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      <textarea placeholder="Write the email…" rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      {error && <p className="text-amber-300 text-xs">{error}</p>}
      <button disabled={pending || !f.to || !f.subject || !f.body} className="bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60">{pending ? "Sending…" : "Send email"}</button>
    </form>
  );
}
function TaskForm({ onSubmit, pending }: { onSubmit: (v: any) => void; pending: boolean }) {
  const [f, setF] = useState({ title: "", due_at: "", priority: "normal" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (f.title.trim()) { onSubmit({ ...f, due_at: f.due_at ? new Date(f.due_at).toISOString() : null }); setF({ title: "", due_at: "", priority: "normal" }); } }} className="space-y-3">
      <input placeholder="Task title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500" />
      <div className="grid grid-cols-2 gap-3">
        <input type="datetime-local" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200" />
        <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200">
          <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
        </select>
      </div>
      <button disabled={pending || !f.title.trim()} className="bg-amber-300 text-zinc-950 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60">Create task</button>
    </form>
  );
}